/**
 * Namespaced flow-state storage. All resumable-flow drafts live under
 * `tkf.flow.<flowKey>` so a single sweep clears the whole namespace on
 * logout or 401.
 *
 * Defers to sessionStorage by design — flow drafts shouldn't survive a
 * browser close, and they shouldn't be visible across tabs (a different
 * tab may be in a different flow stage).
 */
const NAMESPACE = 'tkf.flow';

function fullKey(flowKey: string): string {
  return `${NAMESPACE}.${flowKey}`;
}

interface Envelope<T> {
  value: T;
  /** Wall-clock timestamp at which the draft was written. */
  writtenAt: number;
}

export const flowStorage = {
  read<T>(flowKey: string): T | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.sessionStorage.getItem(fullKey(flowKey));
      if (!raw) return null;
      const env = JSON.parse(raw) as Envelope<T>;
      return env.value;
    } catch {
      return null;
    }
  },

  /** Returns the writtenAt timestamp (ms epoch) or null if no draft. */
  readAge(flowKey: string): number | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.sessionStorage.getItem(fullKey(flowKey));
      if (!raw) return null;
      const env = JSON.parse(raw) as Envelope<unknown>;
      return Number.isFinite(env.writtenAt) ? env.writtenAt : null;
    } catch {
      return null;
    }
  },

  write<T>(flowKey: string, value: T): void {
    if (typeof window === 'undefined') return;
    const env: Envelope<T> = { value, writtenAt: Date.now() };
    try {
      window.sessionStorage.setItem(fullKey(flowKey), JSON.stringify(env));
    } catch {
      // Quota exceeded / serialization error — silent. Recovery is a UX
      // nice-to-have, never a requirement to use the app.
    }
  },

  clear(flowKey: string): void {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(fullKey(flowKey));
  },

  /** Wipe every `tkf.flow.*` entry. Hook for logout / 401 cleanup. */
  clearAll(): void {
    if (typeof window === 'undefined') return;
    const prefix = `${NAMESPACE}.`;
    const toRemove: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (key?.startsWith(prefix)) toRemove.push(key);
    }
    for (const key of toRemove) {
      window.sessionStorage.removeItem(key);
    }
  },
};
